import { ConfirmationDialogAllSingle } from '../TaskScheduler/ConfirmationDialogAllSingle'
import { EventEditDeletePopup } from '../TaskScheduler/TaskSchedulerEventEditDeletePopup'
import type { DialogTarget } from '../TaskScheduler/TaskSettingsModal/DialogContext'
import TaskSettingsModal from '../TaskScheduler/TaskSettingsModal/TaskSettingsModal'
import CloseIcon from '@mui/icons-material/Close'
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import StopIcon from '@mui/icons-material/Stop'
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined'
import SyncIcon from '@mui/icons-material/Sync'
import {
  Box,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material'
import { MainApi } from 'api/MainApi'
import { RobotApi } from 'api/RobotApi'
import { MainService, type OdysseyRead, RobotRead, TaskTypeEnum } from 'api/schema'
import clsx from 'clsx'
import { Button as CustomButton } from 'components/Button/Button'
import Dropdown, { IOption } from 'components/Dropdown/Dropdown'
import GausiumTaskReportView from 'components/GausiumTaskReportView/GausiumTaskReportView'
import { isGausiumTaskReportRow } from 'components/GausiumTaskReportView/gausiumTaskReportUtils'
import Loader from 'components/Loader/Loader'
import Title from 'components/Title/Title'
import addDays from 'date-fns/addDays'
import endOfDay from 'date-fns/endOfDay'
import endOfMonth from 'date-fns/endOfMonth'
import endOfWeek from 'date-fns/endOfWeek'
import format from 'date-fns/format'
import getDay from 'date-fns/getDay'
import isValid from 'date-fns/isValid'
import enUS from 'date-fns/locale/en-US'
import parse from 'date-fns/parse'
import startOfDay from 'date-fns/startOfDay'
import startOfMonth from 'date-fns/startOfMonth'
import startOfWeek from 'date-fns/startOfWeek'
import { dedupeNormalizedApiRows } from 'features/tasksNormalized/dedupeNormalizedApiRows'
import { computeSchedulerFilterOptions, partialMatches } from 'features/tasksNormalized/filterEngine'
import type {
  FilterEngineContext,
  FilterableRecord,
  NormalizedStatisticsApiRow,
  TasksNormalizedFilterState,
} from 'features/tasksNormalized/filterableRecord'
import { filterableRecordToProcessedEvent } from 'features/tasksNormalized/filterableToCalendarEvent'
import {
  GAUSIUM_GAP_DEBOUNCE_MS,
  GAUSIUM_GAP_FIRST_POLL_MS,
  GAUSIUM_GAP_MAX_TICKS,
  GAUSIUM_GAP_POLL_MS,
  buildDeltaCursorFromRows,
  runGausiumGapRefreshFollowup,
} from 'features/tasksNormalized/gausiumGapRefreshFollowup'
import {
  enrichFilterableFacilityIds,
  normalizedStatisticsRowToFilterable,
} from 'features/tasksNormalized/normalizedToFilterable'
import { resolveRobotIdsFromFilters } from 'features/tasksNormalized/resolveRobotIdsFromFilters'
import { FAVORITES_DROPDOWN_VALUE, narrowToFavoriteIds, sortWithFavoritesFirst } from 'utils/favoritesSort'
import { useIsMobile } from 'hooks/useIsMobile'
import { useUrlQueryState } from 'hooks/useUrlQueryState'
import { useUser } from 'hooks/user'
import { exportScheduleToCSV } from 'pages/TaskScheduler/TaskSchedulerExport'
import {
  ProcessedEvent,
  getColorByColorName,
  getColorById,
  getLoadingStatus,
  getTaskId,
  getTaskPrefix,
  renderMissions,
  renderScenario,
  renderStatsTable,
  scheduleToEvents,
  toISOStringYYYYMMDD,
} from 'pages/TaskScheduler/TaskSchedulerUtils'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Calendar, View, Views, dateFnsLocalizer } from 'react-big-calendar'
import { useTranslation } from 'react-i18next'
import { GAUSIUM_ENABLED, PATH, TASK_STATUSES } from 'utils/constants'
import { hasRecurringWeekdays } from 'utils/odysseyReschedule'
import { TASK_CANCEL_REASON_OPTIONS, TASK_CANCEL_REASON_OTHER } from 'utils/taskCancelReasons'
import { flagParam, optionalStringParam } from 'utils/urlQueryState'
import { parseCalendarView, serializeCalendarView, type CalendarViewName } from 'utils/urlViewParams'
import { VENDOR, getVendorDisplayName } from 'utils/vendorDisplayName'
import type { VendorCode } from 'utils/vendorDisplayName'

import schedulerStyle from '../TaskScheduler/SchedulerSettings.module.css'
import style from 'components/Button/Button.module.css'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import mapPageFilterDropdown from 'styles/mapPageFilterDropdown.module.css'
import nonBlockingSyncLoaderStyle from 'styles/nonBlockingSyncLoader.module.css'

const locales = {
  'en-US': enUS,
}

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
})

const calendarFormats = {
  timeGutterFormat: 'HH:mm',
  eventTimeRangeFormat: ({ start, end }: any, culture: any, localizer: any) =>
    localizer.format(start, 'HH:mm', culture) + ' - ' + localizer.format(end, 'HH:mm', culture),
  agendaTimeRangeFormat: ({ start, end }: any, culture: any, localizer: any) =>
    localizer.format(start, 'HH:mm', culture) + ' - ' + localizer.format(end, 'HH:mm', culture),
}

enum EventScopes {
  ALL = 'all',
  CURRENT = 'current',
}

function calendarViewFromName(name: CalendarViewName | '', isMobile: boolean): View {
  if (name === 'week') return Views.WEEK
  if (name === 'day') return Views.DAY
  if (name === 'agenda') return Views.AGENDA
  if (name === 'month') return Views.MONTH
  return isMobile ? Views.AGENDA : Views.MONTH
}

function calendarViewToName(view: View): CalendarViewName {
  if (view === Views.WEEK) return 'week'
  if (view === Views.DAY) return 'day'
  if (view === Views.AGENDA) return 'agenda'
  return 'month'
}

function parseCalendarVendor(raw: string | null): VendorCode | '' {
  const value = (raw || '').trim().toUpperCase()
  if (value === VENDOR.AUTONOMICS || value === VENDOR.GAUSIUM || value === VENDOR.ASBIS) return value
  return ''
}

function coercePositiveInt(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value
  if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
    const n = Number(value.trim())
    return n > 0 ? n : null
  }
  if (value && typeof value === 'object' && 'id' in value) {
    return coercePositiveInt((value as { id?: unknown }).id)
  }
  return null
}

const TaskEventViewer = ({ event }: { event: any }) => {
  const [selectedReport, setSelectedReport] = useState<string>('Overall')

  const extendedProps = event.extendedProps
  const hasExtendedProps =
    extendedProps && extendedProps.missions_progress && extendedProps.missions_progress.length > 0
  const isOdysseyEvent = String(extendedProps?.task_type ?? '')
    .toLowerCase()
    .includes('odyssey')
  const missionInfoList = Array.isArray(extendedProps?.missions_info) ? extendedProps.missions_info : []

  const handleReportChange = (event: SelectChangeEvent) => {
    setSelectedReport(event.target.value as string)
  }

  const taskId = coercePositiveInt(extendedProps?.task_id)
  const progressMissionIds = (Array.isArray(extendedProps?.missions_progress) ? extendedProps.missions_progress : [])
    .map((mission: any) => coercePositiveInt(mission?.id))
    .filter((id: number | null): id is number => id != null && id !== taskId)
  const infoMissionIds = missionInfoList
    .map((m: any) => coercePositiveInt(m?.id))
    .filter((id: number | null): id is number => id != null)
  const fallbackMissionIds = Array.isArray(event.extendedProps?.missions)
    ? event.extendedProps.missions
    : event.extendedProps?.mission_id
    ? [event.extendedProps.mission_id]
    : []
  const eventMissionsRaw = progressMissionIds.length
    ? progressMissionIds
    : infoMissionIds.length
    ? infoMissionIds
    : fallbackMissionIds
  const eventMissions = eventMissionsRaw
    .map((id: any) => coercePositiveInt(id))
    .filter((id: number | null): id is number => id != null)
  const hasMissionReportDropdown = Boolean(
    hasExtendedProps && isOdysseyEvent && missionInfoList.length > 1 && extendedProps.missions_progress.length > 1
  )
  const isNormalizedSingleMission =
    Boolean(event.extendedProps?.normalized) &&
    Array.isArray(event.extendedProps?.missions_info) &&
    event.extendedProps.missions_info.length === 1

  let mainDescription = event.description
  if (hasExtendedProps && selectedReport !== 'Overall') {
    mainDescription = ''
  }

  const currentStats =
    hasExtendedProps &&
    (selectedReport === 'Overall'
      ? extendedProps.overall_progress
      : extendedProps.missions_progress[Number(selectedReport)])

  const currentName =
    hasExtendedProps &&
    (selectedReport === 'Overall'
      ? extendedProps.overall_progress?.missions?.length === 1
        ? extendedProps.missions_progress?.[0]?.name
        : ''
      : extendedProps.missions_progress?.[Number(selectedReport)]?.name)

  const isDeleted = extendedProps?.status === 'deleted'
  const showGausiumReportActions =
    GAUSIUM_ENABLED &&
    Boolean(extendedProps?.normalized) &&
    isGausiumTaskReportRow(extendedProps?.vendor_origin_record_type)
  const gausiumReportId = String(extendedProps?.task_id ?? extendedProps?.source_event_id ?? '').trim()
  const gausiumRobotSn = String(extendedProps?.robot_sn ?? '').trim()

  const displayOffsetMinutes = (event as any).displayOffsetMinutes
  const taskOffsetMinutes = extendedProps?.taskOffsetMinutes

  const isTimezoneDifferent =
    displayOffsetMinutes !== undefined && taskOffsetMinutes !== undefined && displayOffsetMinutes !== taskOffsetMinutes
  const timezoneLabel = isTimezoneDifferent ? ` (${formatUtcOffset(taskOffsetMinutes).replace('UTC', '')})` : ''

  let displayStart = event.start
  let displayEnd = event.end

  if (displayOffsetMinutes !== undefined && taskOffsetMinutes !== undefined) {
    const shiftBackMs = (displayOffsetMinutes - taskOffsetMinutes) * 60_000
    displayStart = new Date(event.start.getTime() - shiftBackMs)
    displayEnd = new Date(event.end.getTime() - shiftBackMs)
  }

  const startTime = format(displayStart, 'HH:mm')
  const endTime = format(displayEnd, 'HH:mm')
  const startDateStr = format(displayStart, 'EEEE, MMM do')
  const endDateStr = format(displayEnd, 'EEEE, MMM do')

  return (
    <div className={style.taskEventBlock}>
      <Box sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#666' }}>
            <PlayArrowIcon sx={{ fontSize: '1rem', flexShrink: 0 }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {startDateStr}, {startTime}
              {timezoneLabel}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#666' }}>
            <StopIcon sx={{ fontSize: '1rem', flexShrink: 0 }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {endDateStr}, {endTime}
              {timezoneLabel}
            </Typography>
          </Box>
        </Box>
        {showGausiumReportActions && gausiumReportId && gausiumRobotSn ? (
          <GausiumTaskReportView robotSn={gausiumRobotSn} reportId={gausiumReportId} startAt={event.start} />
        ) : null}
      </Box>

      {hasMissionReportDropdown && (
        <Box sx={{ minWidth: 120, mb: 1, mt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="report-select-label">Report</InputLabel>
            <Select
              labelId="report-select-label"
              id="report-select"
              value={selectedReport}
              label="Report"
              onChange={handleReportChange}
            >
              <MenuItem value={'Overall'}>Overall</MenuItem>
              {extendedProps.missions_progress.map((mission: any, index: number) => {
                const progressId = coercePositiveInt(mission?.id)
                const reportedId = progressId != null && progressId !== taskId ? progressId : null
                const info = missionInfoList[index]
                const infoId = coercePositiveInt(info?.id)
                const missionId = reportedId ?? infoId
                const missionName =
                  infoId != null && infoId === missionId ? info?.name ?? mission?.name : mission?.name ?? info?.name
                return (
                  <MenuItem key={`${mission.id}_${index}`} value={index.toString()}>
                    {`${missionId ?? ''}${missionName ? ` ${missionName}` : ''}`.trim()}
                  </MenuItem>
                )
              })}
            </Select>
          </FormControl>
        </Box>
      )}
      {isDeleted && (
        <Box
          sx={{
            mt: 1,
            pt: 1,
            borderTop: '1px solid var(--form-border)',
            mb: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 'bold', mb: 0.5, color: 'var(--text-secondary)', fontSize: '0.875rem' }}
          >
            {event.extendedProps.mission_status === 8
              ? 'The task was aborted'
              : event.extendedProps.mission_status === 9
              ? 'The task is delayed'
              : event.extendedProps.mission_status === 99 || event.extendedProps.mission_status === 10
              ? 'The task was not started'
              : 'The task was canceled'}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.875rem', mb: 0.0 }}>
            <span style={{ fontWeight: 'bold', color: 'var(--text-main)' }}>Reason: </span>
            {event.extendedProps.exception_reason}
          </Typography>
          {event.extendedProps.exception_user && (
            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
              <span style={{ fontWeight: 'bold', color: 'var(--text-main)' }}>
                {event.extendedProps.mission_status === 8
                  ? 'Aborted by: '
                  : event.extendedProps.mission_status === 9
                  ? 'Delayed by: '
                  : 'Canceled by: '}
              </span>
              {event.extendedProps.exception_user}
            </Typography>
          )}
        </Box>
      )}
      {!isDeleted && extendedProps?.mission_status === 99 && (
        <Box
          sx={{
            mt: 1,
            pt: 1,
            borderTop: '1px solid var(--form-border)',
            mb: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 'bold', mb: 0.5, color: 'var(--text-secondary)', fontSize: '0.875rem' }}
          >
            The task was not launched — no data from the robot.
          </Typography>
        </Box>
      )}
      {mainDescription && <Box sx={{ fontSize: '0.875rem', mb: 0 }}>{mainDescription}</Box>}
      {hasMissionReportDropdown && selectedReport === 'Overall' && eventMissions.length > 0 && (
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          <span style={{ fontWeight: 'bold' }}>Missions:</span>{' '}
          {eventMissions.map((missionId: number, idx: number) => (
            <React.Fragment key={missionId}>
              {idx > 0 ? ', ' : ''}
              <a className={schedulerStyle.link} href={`${PATH.MISSION}${missionId}`} target="_blank" rel="noreferrer">
                {missionId}
              </a>
            </React.Fragment>
          ))}
        </Typography>
      )}
      {selectedReport === 'Overall' && !hasMissionReportDropdown && renderMissions(eventMissions)}

      {currentStats &&
        (() => {
          const reportContent = renderStatsTable(currentStats)
          if (!reportContent) return null
          return (
            <Box>
              {selectedReport === 'Overall' && currentName && !isNormalizedSingleMission && (
                <Typography variant="body2" sx={{ mb: 0.0 }}>
                  <span style={{ fontWeight: 'bold' }}>Mission name:</span> {currentName}
                </Typography>
              )}
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                Report:
              </Typography>
              {reportContent}
            </Box>
          )
        })()}

      {hasExtendedProps && selectedReport !== 'Overall' && (
        <Box sx={{ mt: 1 }}>
          <a
            className={schedulerStyle.link}
            href={`${PATH.MISSION}${extendedProps.missions_progress[Number(selectedReport)]?.id}`}
            target="_blank"
            rel="noreferrer"
          >
            Go to mission
          </a>
        </Box>
      )}
    </div>
  )
}

function rowsToFilterableRecords(
  rows: NormalizedStatisticsApiRow[],
  ridMap: Map<number, number[]>
): FilterableRecord[] {
  const records: FilterableRecord[] = []
  for (const row of rows) {
    const base = normalizedStatisticsRowToFilterable(row)
    if (!base) continue
    records.push(enrichFilterableFacilityIds(base, ridMap))
  }
  return records
}

const buildRobotIdToFacilityIds = (facilities: { id: number; robots_ids?: number[] }[]): Map<number, number[]> => {
  const m = new Map<number, number[]>()
  for (const f of facilities) {
    for (const rid of f.robots_ids || []) {
      if (!m.has(rid)) m.set(rid, [])
      m.get(rid)!.push(f.id)
    }
  }
  return m
}

// --- Timezone display helpers ---
const formatUtcOffset = (offsetMinutes: number): string => {
  const sign = offsetMinutes >= 0 ? '+' : '-'
  const abs = Math.abs(offsetMinutes)
  const h = Math.floor(abs / 60)
  const m = abs % 60
  return `UTC${sign}${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

/** Range used for API fetch — must match what the user sees for `date` + `view`, not RBC's `onRangeChange` (month grid / internal windows differ). */
function computeCalendarDataRange(date: Date, currentView: View): { start: Date; end: Date } {
  if (currentView === Views.MONTH) {
    // Month view grid shows weeks spanning across month boundaries.
    // We must cover the full grid (usually 6 weeks).
    return {
      start: startOfWeek(startOfMonth(date), { weekStartsOn: 0 }),
      end: endOfWeek(endOfMonth(date), { weekStartsOn: 0 }),
    }
  }
  if (currentView === Views.AGENDA) {
    return { start: startOfDay(date), end: addDays(date, 1) }
  }
  if (currentView === Views.WEEK) {
    return { start: startOfWeek(date, { weekStartsOn: 0 }), end: endOfWeek(date, { weekStartsOn: 0 }) }
  }
  return { start: startOfDay(date), end: endOfDay(date) }
}

function clampPlannedEventsRange(start: Date, end: Date): { start: Date; end: Date } | null {
  const plannedStart = new Date(Math.max(start.getTime(), new Date().getTime()))
  if (plannedStart > end) return null
  return { start: plannedStart, end }
}

/** Slow plan refresh while the page stays open. Facts and the robot catalog are not on this timer. */
const ODYSSEY_PLAN_POLL_MS = 20_000
/** Faster while any cached template is still Loading / Not Loaded, so the badge can catch `scenario_calculating` → `scenario_done`. */
const ODYSSEY_PLAN_POLL_FAST_MS = 5_000

function odysseyNeedsLiveStatusPoll(rows: OdysseyRead[]): boolean {
  return rows.some(row => {
    if (row.job_type === TaskTypeEnum.DELETE_SCENARIO) return false
    const status = row.status
    return status === 'new' || status === 'scenario_calculating' || status === 'scenario_error'
  })
}

/** Template fields that change a planned card or the Loading / Not Loaded badge. Wall clock is not included — clamp vs `now` is applied separately when regenerating events. */
function odysseyPlanSignature(rows: OdysseyRead[]): string {
  return rows
    .map(row => {
      const settings = row.scheduler_settings
      return JSON.stringify({
        id: row.id,
        robot_id: row.robot_id,
        status: row.status ?? '',
        job_type: row.job_type,
        name: row.name ?? '',
        scenario: row.scenario ?? null,
        missions: [...(row.missions ?? [])].slice().sort((a, b) => a - b),
        execution_time: row.execution_time ?? null,
        predicted_duration: row.predicted_duration ?? null,
        datetime: settings?.datetime ?? '',
        timezone: settings?.timezone ?? '',
        weekdays: settings?.weekdays ?? null,
        except_days: settings?.except_days ?? [],
        except_days_descriptions: settings?.except_days_descriptions ?? null,
        except_days_users: settings?.except_days_users ?? null,
      })
    })
    .sort()
    .join('\n')
}

function plannedEventsSignature(events: ProcessedEvent[]): string {
  return events
    .map(event => {
      const props = event.extendedProps || {}
      return [
        String(event.event_id),
        event.start.getTime(),
        event.end.getTime(),
        String(event.title ?? ''),
        String(props.liveStatus ?? ''),
        String(props.status ?? ''),
        String(props.task_id ?? ''),
      ].join('|')
    })
    .sort()
    .join('\n')
}

/** Live facts while the page stays open. Not a full-range reload and not a Gausium gap-refresh. */
const FACTS_POLL_MS = 20_000
const FACTS_WINDOW_BEFORE_MS = 2 * 60 * 60 * 1000
const FACTS_WINDOW_AFTER_MS = 60 * 60 * 1000

function factRowKey(row: NormalizedStatisticsApiRow): string {
  return `${row.vendor_origin_record_type ?? ''}:${String(row.event_id ?? '')}`
}

function factsSignature(rows: NormalizedStatisticsApiRow[]): string {
  return rows
    .map(row =>
      [
        factRowKey(row),
        String(row.start_time ?? ''),
        String(row.end_time ?? ''),
        String(row.execution_status ?? ''),
        String(row.completion_percentage ?? ''),
        String(row.cancellation_reason ?? ''),
        String(row.name ?? ''),
        String(row.duration_actual ?? ''),
      ].join('|')
    )
    .sort()
    .join('\n')
}

function mergeFactsByEventId(
  existing: NormalizedStatisticsApiRow[],
  incoming: NormalizedStatisticsApiRow[]
): NormalizedStatisticsApiRow[] {
  const byId = new Map<string, NormalizedStatisticsApiRow>()
  for (const row of existing) {
    const key = factRowKey(row)
    if (key.endsWith(':')) continue
    byId.set(key, row)
  }
  for (const row of incoming) {
    const key = factRowKey(row)
    if (key.endsWith(':')) continue
    byId.set(key, row)
  }
  return Array.from(byId.values())
}

function hotFactsWindow(now = new Date()): { start: Date; end: Date } {
  return {
    start: new Date(now.getTime() - FACTS_WINDOW_BEFORE_MS),
    end: new Date(now.getTime() + FACTS_WINDOW_AFTER_MS),
  }
}

function rangesOverlap(aStart: Date, aEnd: Date, bStart: Date, bEnd: Date): boolean {
  return aStart.getTime() < bEnd.getTime() && bStart.getTime() < aEnd.getTime()
}

const TaskSchedulerNormalized = () => {
  const { t } = useTranslation()
  const user = useUser()
  const [refreshKey, setRefreshKey] = useState(0)
  const isLoadingEvents = useRef(false)
  const [eventScope, setEventScope] = useState<string>(EventScopes.CURRENT)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [isEditConfirmationOpen, setIsEditConfirmationOpen] = useState(false)
  const [isSingleTaskEditing, setSingleTaskEditing] = useState(false)
  const [openDeleteScheduleDialog, setOpenDeleteScheduleDialog] = useState(false)
  const [deleteReason, setDeleteReason] = useState<string>('')
  const [deleteDescription, setDeleteDescription] = useState('')
  const [selectedEvent, setSelectedEvent] = useState<ProcessedEvent | null>(null)
  const [hasAutonomicsRobotsAvailable, setHasAutonomicsRobotsAvailable] = useState<boolean | null>(null)
  const hasAppliedInitialVendorDefaultRef = useRef(false)

  const [url, setUrl] = useUrlQueryState({
    vendor: {
      parse: parseCalendarVendor,
      serialize: (value: VendorCode | '') => value || undefined,
    },
    facility: optionalStringParam(),
    robot: optionalStringParam(),
    rfav: flagParam(),
    mission: optionalStringParam(),
    startAfter: {
      parse: raw => {
        const value = Number(raw)
        return Number.isInteger(value) && value >= 0 && value <= 23 ? value : -1
      },
      serialize: (value: number) => (value >= 0 ? String(value) : undefined),
    },
    date: {
      parse: raw => {
        if (!raw) return ''
        const parsed = parse(raw, 'yyyy-MM-dd', new Date())
        return isValid(parsed) ? format(parsed, 'yyyy-MM-dd') : ''
      },
      serialize: (value: string) => value || undefined,
    },
    view: {
      parse: parseCalendarView,
      serialize: serializeCalendarView,
    },
  })

  const filterState: TasksNormalizedFilterState = useMemo(
    () => ({
      vendor: url.vendor,
      facilityId: url.facility,
      robotSn: url.robot,
      missionKey: url.mission,
      startAfterHour: url.startAfter,
    }),
    [url.vendor, url.facility, url.robot, url.mission, url.startAfter]
  )
  const favoriteRobotIds = useMemo(() => user?.state.favoriteRobotIds ?? [], [user?.state.favoriteRobotIds])
  const favoritesOnly = url.rfav && !url.robot.trim()

  const setFilterState = useCallback(
    (patch: TasksNormalizedFilterState | ((prev: TasksNormalizedFilterState) => TasksNormalizedFilterState)) => {
      setUrl(s => {
        const current: TasksNormalizedFilterState = {
          vendor: s.vendor,
          facilityId: s.facility,
          robotSn: s.robot,
          missionKey: s.mission,
          startAfterHour: s.startAfter,
        }
        const next = typeof patch === 'function' ? patch(current) : patch
        return {
          vendor: next.vendor,
          facility: next.facilityId,
          robot: next.robotSn,
          mission: next.missionKey,
          startAfter: next.startAfterHour,
        }
      })
    },
    [setUrl]
  )
  const [displayOffsetMinutes, setDisplayOffsetMinutes] = useState<number>(() => -new Date().getTimezoneOffset())

  const dataCache = useRef({
    robots: [] as RobotRead[],
    odysseys: [] as OdysseyRead[],
    missionsMap: new Map<number, string>(),
    facilities: [] as { id: number; name?: string | null; robots_ids?: number[] }[],
    robotIdToFacilityIds: new Map<number, number[]>(),
    fetched: { robots: false, missions: false, facilities: false },
  })

  /** Robots, facilities, missions, scenarios — once per page open, not on every range change. */
  const [catalogsReady, setCatalogsReady] = useState(false)
  /** Bumps when the robot/facility catalog ref is replaced, so memos can read it. */
  const [catalogVersion, setCatalogVersion] = useState(0)
  const [normalizedRecords, setNormalizedRecords] = useState<FilterableRecord[]>([])
  const [plannedEvents, setPlannedEvents] = useState<ProcessedEvent[]>([])
  const [isGausiumSyncing, setIsGausiumSyncing] = useState(false)
  /** Mission/scenario id→name for filter dropdown labels (same catalogs as History panel). */
  const [filterLabelCatalogs, setFilterLabelCatalogs] = useState<{
    missions: Record<number, string>
    scenarios: Record<number, string>
  }>({ missions: {}, scenarios: {} })
  /** Incremented per load; background GS refresh and the odyssey poll ignore stale generations after navigation. */
  const normalizedFetchGenRef = useRef(0)
  /** Set after the first range load, so the plan poll does not race the initial fetch. */
  const plansLoadedRef = useRef(false)
  const odysseyPlanSigRef = useRef('')
  const plannedEventSigRef = useRef('')
  const odysseyPollInFlightRef = useRef(false)
  const pollOdysseyPlansRef = useRef<() => Promise<void>>(async () => {})
  const pollFactsRef = useRef<() => Promise<void>>(async () => {})
  /** Full fact rows for the loaded range. Hot poll and GS delta merge into this by event id. */
  const factRowsRef = useRef<NormalizedStatisticsApiRow[]>([])
  const factSigRef = useRef('')
  const factsPollInFlightRef = useRef(false)

  const isMobile = useIsMobile()
  const selectedDate = useMemo(() => {
    if (!url.date) return new Date()
    const parsed = parse(url.date, 'yyyy-MM-dd', new Date())
    return isValid(parsed) ? parsed : new Date()
  }, [url.date])
  const setSelectedDate = useCallback(
    (date: Date) => {
      const serialized = format(date, 'yyyy-MM-dd')
      setUrl({ date: serialized === format(new Date(), 'yyyy-MM-dd') ? '' : serialized })
    },
    [setUrl]
  )
  const view = calendarViewFromName(url.view, isMobile)
  const setView = useCallback(
    (nextView: View) => setUrl({ view: calendarViewToName(nextView) }),
    [setUrl]
  )
  const [showFilters, setShowFilters] = useState(false)

  const currentRange = useMemo(() => computeCalendarDataRange(selectedDate, view), [selectedDate, view])
  const calendarTimeBounds = useMemo(
    () => ({
      min: startOfDay(selectedDate),
      max: endOfDay(selectedDate),
      scrollToTime: startOfDay(selectedDate),
    }),
    [selectedDate]
  )

  const handleViewChange = useCallback(
    (newView: View) => {
      setView(newView)
    },
    [setView]
  )

  const handleNavigate = useCallback(
    (newDate: Date) => {
      setSelectedDate(newDate)
    },
    [setSelectedDate]
  )

  const engineCtx: FilterEngineContext = useMemo(() => {
    // catalogVersion invalidates this memo when the ref-backed robot/facility cache is replaced.
    const robots = catalogVersion >= 0 ? dataCache.current.robots : []
    const facilities = catalogVersion >= 0 ? dataCache.current.facilities : []
    const robotsBySn = new Map<string, { id: number; name: string; sn: string }>()
    for (const r of robots) {
      const sn = String(r.serial_number ?? '').trim()
      if (!sn) continue
      robotsBySn.set(sn, {
        id: r.id,
        name: String(r.name ?? '').trim() || sn,
        sn,
      })
    }
    return {
      facilityMappingReady: dataCache.current.fetched.facilities,
      facilities: facilities.map(f => ({
        id: f.id,
        name: String(f.name ?? '').trim() || `Facility ${f.id}`,
      })),
      robotsBySn,
      missionNamesById: filterLabelCatalogs.missions,
      scenarioNamesById: filterLabelCatalogs.scenarios,
    }
  }, [catalogVersion, filterLabelCatalogs])

  const favoriteSnSet = useMemo(() => {
    const ids = new Set(favoriteRobotIds)
    const sns = new Set<string>()
    Array.from(engineCtx.robotsBySn.values()).forEach(robot => {
      if (ids.has(robot.id)) sns.add(robot.sn)
    })
    return sns
  }, [favoriteRobotIds, engineCtx.robotsBySn])

  const scopedRobotIds = useCallback(
    (state: TasksNormalizedFilterState) => {
      const ids = resolveRobotIdsFromFilters(state, dataCache.current.robots, dataCache.current.facilities)
      if (!favoritesOnly) return ids
      return narrowToFavoriteIds(ids, favoriteRobotIds)
    },
    [favoritesOnly, favoriteRobotIds]
  )

  const fetchInitialData = async (force: boolean) => {
    const promises: Promise<void>[] = []
    if (force || !dataCache.current.fetched.robots) {
      promises.push(
        MainService.mainRobotList(['-id'], 1, 1000).then(res => {
          const robots = res.results ?? []
          dataCache.current.robots = robots
          setHasAutonomicsRobotsAvailable(robots.some(robot => robot.vendor === VENDOR.AUTONOMICS))
          dataCache.current.fetched.robots = true
        })
      )
    }
    if (force || !dataCache.current.fetched.facilities) {
      promises.push(
        MainApi.fetchFacilities(1, 1000).then(res => {
          dataCache.current.facilities = res.results ?? []
          dataCache.current.robotIdToFacilityIds = buildRobotIdToFacilityIds(dataCache.current.facilities)
          dataCache.current.fetched.facilities = true
        })
      )
    }
    if (force || !dataCache.current.fetched.missions) {
      promises.push(
        Promise.all([MainService.mainMissionList(['-id'], 1, 1000), MainApi.fetchScenarios(1, 3000)]).then(
          ([missionRes, scenRes]) => {
            const map = new Map<number, string>()
            const missionsRec: Record<number, string> = {}
            missionRes.results?.forEach(m => {
              if (m.name) {
                map.set(m.id, m.name)
                missionsRec[m.id] = m.name
              }
            })
            dataCache.current.missionsMap = map
            dataCache.current.fetched.missions = true
            const scenariosRec: Record<number, string> = {}
            scenRes.results?.forEach((s: { id?: number; name?: string }) => {
              if (typeof s.id === 'number' && typeof s.name === 'string') {
                scenariosRec[s.id] = s.name
              }
            })
            setFilterLabelCatalogs({ missions: missionsRec, scenarios: scenariosRec })
          }
        )
      )
    }
    if (promises.length) {
      await Promise.all(promises)
      setCatalogVersion(v => v + 1)
    }
  }

  React.useEffect(() => {
    let cancelled = false
    ;(async () => {
      await fetchInitialData(false)
      if (!cancelled) setCatalogsReady(true)
    })()
    return () => {
      cancelled = true
    }
    // Catalogs are loaded once; refreshKey refetches them inside getRemoteEvents.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const generatePlannedEventsFromRobots = useCallback(
    (start: Date, end: Date): ProcessedEvent[] => {
      const eventsSet: ProcessedEvent[] = []
      const robotById = new Map(dataCache.current.robots.map(robot => [robot.id, robot]))
      for (const odyssey of dataCache.current.odysseys) {
        const robot = robotById.get(odyssey.robot_id)
        if (!robot) continue
        const facilityIds = dataCache.current.robotIdToFacilityIds.get(robot.id) ?? []
        if (odyssey.job_type === TaskTypeEnum.DELETE_SCENARIO) continue

          const buildEvents = (rangeStart: Date, rangeEnd: Date) =>
            scheduleToEvents(
              rangeStart,
              rangeEnd,
              odyssey,
              robot.robot_color
                ? () => getColorByColorName(robot.robot_color || 'White')
                : () => getColorById(robot.id),
              <React.Fragment>{odyssey?.scenario ? renderScenario(odyssey.scenario) : null}</React.Fragment>,
              robot,
              dataCache.current.missionsMap,
              facilityIds,
              displayOffsetMinutes
            ).map(e => {
              // scheduleToEvents uses task_type='Task' for Odyssey; normalize here so
              // planned-vs-executed dedup works and UI labels are consistent.
              e.extendedProps = {
                ...(e.extendedProps || {}),
                source_kind: 'planned',
                task_type: 'Odyssey',
                liveStatus: odyssey.status,
              }
              if (e.extendedProps?.status === 'deleted') {
                e.extendedProps.statusPrefix = React.createElement(StopCircleOutlinedIcon, {
                  style: { fontSize: '1.3em' },
                  color: 'inherit',
                })
              }
              return e
            })

          const plannedRange = clampPlannedEventsRange(start, end)
          if (plannedRange) {
            const events = buildEvents(plannedRange.start, plannedRange.end)
            // For the future, we show both active planned events and 'deleted' (canceled) schedule items.
            eventsSet.push(...events.filter(e => e.start >= plannedRange.start))
          }
      }
      return eventsSet
    },
    [displayOffsetMinutes]
  )

  const plannedEventToFilterable = useCallback((e: ProcessedEvent): FilterableRecord | null => {
    const props = e.extendedProps || {}
    const robotSn = String(props.robot_sn ?? '').trim()
    if (!robotSn) return null
    const missionId = typeof props.mission_id === 'number' ? props.mission_id : null
    const scenarioId = coercePositiveInt(props.scenario) ?? coercePositiveInt(props.scenario_id)
    const missionsInfo = Array.isArray(props.missions_info)
      ? props.missions_info
          .map((m: any) => ({
            id: Number(m?.id),
            name: typeof m?.name === 'string' ? m.name : undefined,
          }))
          .filter((m: { id: number }) => Number.isFinite(m.id))
      : undefined
    const missionsInfoIds = missionsInfo ? missionsInfo.map((m: { id: number }) => m.id) : []
    return {
      vendor: props.vendor,
      robotSn,
      robotId: typeof props.robot_id === 'number' ? props.robot_id : null,
      facilityIds: Array.isArray(props.facility_ids) ? props.facility_ids.filter((x: any) => Number.isFinite(x)) : [],
      missionId,
      scenarioId,
      mapId: typeof props.map_id === 'string' && props.map_id.trim() ? props.map_id.trim() : null,
      missionsInfoIds,
      missionsInfo,
      taskId: props.task_id ?? null,
      displayName: e.title as string,
      startAt: e.start,
      endAt: e.end,
      sourceEventId: String(e.event_id),
    }
  }, [])

  const combinedRecords = useMemo(() => {
    const plannedRecords = plannedEvents.map(plannedEventToFilterable).filter((x): x is FilterableRecord => Boolean(x))
    return [...normalizedRecords, ...plannedRecords]
  }, [normalizedRecords, plannedEvents, plannedEventToFilterable])

  const filterOptions = useMemo(
    () => computeSchedulerFilterOptions(combinedRecords, filterState, engineCtx),
    [combinedRecords, filterState, engineCtx]
  )
  const scenarioMissionOptions = useMemo(
    () => filterOptions.missions.filter(m => m.kind === 'scenario'),
    [filterOptions.missions]
  )
  const missionOptions = useMemo(
    () => filterOptions.missions.filter(m => m.kind === 'mission'),
    [filterOptions.missions]
  )
  const mapOptions = useMemo(() => filterOptions.missions.filter(m => m.kind === 'map'), [filterOptions.missions])

  const missionFilterDropdownOptions: IOption[] = useMemo(() => {
    const opts: IOption[] = []
    const scenarioGroup = t('dashboard.filters.scenarios_group')
    const missionGroup = t('dashboard.filters.missions_group')
    const mapGroup = t('dashboard.filters.maps_group')
    opts.push({ value: '', text: 'All Missions' })
    scenarioMissionOptions.forEach((m, i) => {
      opts.push({ value: m.key, text: m.label, subtitle: i === 0 ? scenarioGroup : undefined })
    })
    missionOptions.forEach((m, i) => {
      opts.push({ value: m.key, text: m.label, subtitle: i === 0 ? missionGroup : undefined })
    })
    mapOptions.forEach((m, i) => {
      opts.push({ value: m.key, text: m.label, subtitle: i === 0 ? mapGroup : undefined })
    })
    return opts
  }, [scenarioMissionOptions, missionOptions, mapOptions, t])

  const missionFilterCurrentOption = useMemo((): IOption | undefined => {
    if (!filterState.missionKey) return undefined
    const m = filterOptions.missions.find(x => x.key === filterState.missionKey)
    return {
      value: filterState.missionKey,
      text: m?.label || filterState.missionKey,
    }
  }, [filterState.missionKey, filterOptions.missions])

  const missionSelectOptions = useMemo((): IOption[] => {
    const base = missionFilterDropdownOptions
    const mk = filterState.missionKey
    if (!mk) return base
    if (base.some(o => String(o.value) === mk)) return base
    const cur = missionFilterCurrentOption
    if (!cur) return base
    return [base[0], { value: cur.value, text: cur.text }, ...base.slice(1)]
  }, [missionFilterDropdownOptions, missionFilterCurrentOption, filterState.missionKey])

  const robotColorBySn = useMemo(() => {
    const m: Record<string, string | null | undefined> = {}
    const robots = catalogVersion >= 0 ? dataCache.current.robots : []
    for (const r of robots) {
      const sn = String(r.serial_number ?? '').trim()
      if (!sn) continue
      m[sn] = r.robot_color
    }
    return m
  }, [catalogVersion])

  /** Keep selected filters visible when the new calendar range has no matching rows yet (options were only from current data). */
  const facilityDropdownOptions: IOption[] = useMemo(() => {
    const rows = filterOptions.facilityOptions.map(f => ({ value: String(f.id), text: f.name }))
    const fid = filterState.facilityId
    if (fid && !rows.some(o => String(o.value) === String(fid))) {
      const fromCtx = engineCtx.facilities.find(f => String(f.id) === String(fid))
      if (fromCtx) {
        rows.unshift({ value: String(fromCtx.id), text: fromCtx.name })
      }
    }
    return [{ value: '', text: 'All Facilities' }, ...rows]
  }, [filterOptions.facilityOptions, filterState.facilityId, engineCtx.facilities])

  const vendorDropdownOptions: IOption[] = useMemo(() => {
    const rows = filterOptions.vendors.map(v => ({ value: v, text: getVendorDisplayName(v) }))
    const v = filterState.vendor
    if (v && !filterOptions.vendors.includes(v as VendorCode)) {
      rows.unshift({ value: v, text: getVendorDisplayName(v as VendorCode) || v })
    }
    return [{ value: '', text: 'All Vendors' }, ...rows]
  }, [filterOptions.vendors, filterState.vendor])

  const robotDropdownOptions: IOption[] = useMemo(() => {
    const favoriteIds = new Set(favoriteRobotIds)
    const visible = favoritesOnly ? filterOptions.robots.filter(r => favoriteIds.has(r.id)) : filterOptions.robots
    const rows = sortWithFavoritesFirst(visible, favoriteIds).map(r => ({
      value: r.sn,
      text: r.name && r.name !== r.sn ? `${r.sn} - ${r.name}` : r.sn,
    }))
    const sn = filterState.robotSn.trim()
    if (sn && !favoritesOnly && !rows.some(o => o.value === sn)) {
      const ro = engineCtx.robotsBySn.get(sn)
      if (ro) {
        rows.unshift({
          value: ro.sn,
          text: ro.name && ro.name !== ro.sn ? `${ro.sn} - ${ro.name}` : ro.sn,
        })
      }
    }
    return [
      { value: '', text: 'All Robots' },
      { value: FAVORITES_DROPDOWN_VALUE, text: t('favorites.only'), leadingIcon: 'star' as const },
      ...rows,
    ]
  }, [filterOptions.robots, filterState.robotSn, engineCtx.robotsBySn, favoriteRobotIds, favoritesOnly, t])
  const filteredCalendarEvents = useMemo(() => {
    const shiftEventToDisplayTz = (eventStart: Date): Date => {
      const eventOffsetMinutes = -eventStart.getTimezoneOffset()
      const eventShiftMs = (displayOffsetMinutes - eventOffsetMinutes) * 60_000
      return eventShiftMs !== 0 ? new Date(eventStart.getTime() + eventShiftMs) : eventStart
    }

    const norm = normalizedRecords
      .filter(r => {
        if (!partialMatches(r, filterState, { ignoreStartAfter: true }, engineCtx)) return false
        if (favoritesOnly && catalogsReady && !favoriteSnSet.has(r.robotSn.trim())) return false
        if (filterState.startAfterHour >= 0) {
          const displayStart = shiftEventToDisplayTz(r.startAt)
          if (displayStart.getHours() < filterState.startAfterHour) return false
        }
        return true
      })
      .map(r => filterableRecordToProcessedEvent(r, robotColorBySn, engineCtx.robotsBySn.get(r.robotSn)?.name))
      .map(e => ({ ...e, extendedProps: { ...(e.extendedProps || {}), source_kind: 'executed' } }))

    const planned = plannedEvents.filter(e => {
      const rec = plannedEventToFilterable(e)
      if (!rec) return false
      if (!partialMatches(rec, filterState, { ignoreStartAfter: true }, engineCtx)) return false
      if (favoritesOnly && catalogsReady && !favoriteSnSet.has(rec.robotSn.trim())) return false
      if (filterState.startAfterHour >= 0) {
        const displayStart = shiftEventToDisplayTz(e.start)
        if (displayStart.getHours() < filterState.startAfterHour) return false
      }
      return true
    })

    // Same behavior as legacy scheduler: when planned and executed match (task/odyssey + robot + day),
    // keep executed and hide planned to avoid duplicate cards.
    const executedKeys = new Set<string>()
    for (const e of norm) {
      const props = e.extendedProps || {}
      const taskId = props.task_id
      const robotSn = String(props.robot_sn ?? '').trim()
      if (taskId == null || !robotSn) continue
      const taskKind = String(props.task_type ?? 'Task')
        .toLowerCase()
        .includes('odyssey')
        ? 'odyssey'
        : 'task'
      const day = toISOStringYYYYMMDD(e.start)
      executedKeys.add(`${taskKind}:${String(taskId)}:${robotSn}:${day}`)
    }

    const plannedWithoutDuplicates = planned.filter(e => {
      const props = e.extendedProps || {}
      const taskId = props.task_id
      const robotSn = String(props.robot_sn ?? '').trim()
      if (taskId == null || !robotSn) return true
      const taskKind = String(props.task_type ?? 'Task')
        .toLowerCase()
        .includes('odyssey')
        ? 'odyssey'
        : 'task'
      const day = toISOStringYYYYMMDD(e.start)
      const key = `${taskKind}:${String(taskId)}:${robotSn}:${day}`
      return !executedKeys.has(key)
    })

    const finalEvents = [...norm, ...plannedWithoutDuplicates]

    return finalEvents.map(e => {
      const eventOffsetMinutes = -e.start.getTimezoneOffset()
      const eventShiftMs = (displayOffsetMinutes - eventOffsetMinutes) * 60_000
      if (eventShiftMs === 0) return { ...e, displayOffsetMinutes }
      return {
        ...e,
        start: new Date(e.start.getTime() + eventShiftMs),
        end: new Date(e.end.getTime() + eventShiftMs),
        displayOffsetMinutes: displayOffsetMinutes,
      }
    })
  }, [
    normalizedRecords,
    plannedEvents,
    filterState,
    engineCtx,
    robotColorBySn,
    plannedEventToFilterable,
    displayOffsetMinutes,
    favoritesOnly,
    catalogsReady,
    favoriteSnSet,
  ])

  const agendaEvents = useMemo(() => {
    if (view !== Views.AGENDA) return filteredCalendarEvents

    const dayStart = startOfDay(selectedDate).getTime()
    const dayEnd = endOfDay(selectedDate).getTime()

    return filteredCalendarEvents.filter(event => {
      const s = event.start.getTime()
      const e = event.end.getTime()
      return s <= dayEnd && e >= dayStart
    })
  }, [filteredCalendarEvents, view, selectedDate])

  const eventPropGetter = useCallback(
    (event: ProcessedEvent) => ({
      style: {
        backgroundColor: event.color || '#3174ad',
        borderRadius: '0',
        opacity: view === Views.AGENDA ? 0.9 : 1,
        color: 'white',
        border: '0px',
        display: 'block',
      },
    }),
    [view]
  )

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [popoverEvent, setPopoverEvent] = useState<ProcessedEvent | null>(null)

  /** Keep the open popover on the latest calendar row (progress / status) without closing it. */
  const livePopoverEvent = useMemo(() => {
    if (!popoverEvent) return null
    const id = String(popoverEvent.event_id)
    const live = agendaEvents.find(e => String(e.event_id) === id)
    if (live) return live
    const props = popoverEvent.extendedProps || {}
    const taskId = props.task_id
    const robotSn = String(props.robot_sn ?? '').trim()
    if (taskId == null || !robotSn) return popoverEvent
    const byTask = agendaEvents.find(e => {
      const p = e.extendedProps || {}
      return p.task_id === taskId && String(p.robot_sn ?? '').trim() === robotSn
    })
    return byTask ?? popoverEvent
  }, [popoverEvent, agendaEvents])

  const handleEventClick = (event: ProcessedEvent, e: React.SyntheticEvent) => {
    const target = (e.currentTarget || e.target) as HTMLElement
    setAnchorEl(target)
    setPopoverEvent(event)
    setSelectedEvent(event)
  }

  const handleClosePopover = () => {
    setAnchorEl(null)
    setPopoverEvent(null)
  }

  const getTaskById = useCallback((eventId: string): OdysseyRead | undefined => {
    const prefix = getTaskPrefix(eventId)
    const taskId = getTaskId(eventId)
    if (prefix !== 'odyssey') return undefined
    return (
      dataCache.current.odysseys.find(odyssey => odyssey.id === taskId) ??
      dataCache.current.robots.flatMap(robot => robot.odysseys).find((odyssey: OdysseyRead) => odyssey.id === taskId)
    )
  }, [])

  const isAllValueDisabled = (selectedEvent: ProcessedEvent | null): boolean => {
    if (!selectedEvent) return true
    const task = getTaskById(String(selectedEvent.event_id))
    if (!task || !task.scheduler_settings || !task.scheduler_settings.weekdays) return true
    const weekdays = task.scheduler_settings.weekdays
    return !Object.values(weekdays).some(val => val === 'true')
  }

  const onSettingsCloseClicked = useCallback((_refresh: boolean) => {
    setIsSettingsOpen(false)
    setSelectedEvent(null)
  }, [])

  const onSettingsSubmitClicked = useCallback((refresh: boolean) => {
    setIsSettingsOpen(false)
    setSelectedEvent(null)
    if (refresh) setRefreshKey(v => v + 1)
  }, [])

  const handleSubmitDeleteSchedule = async (): Promise<void> => {
    try {
      if (!selectedEvent) {
        throw new Error('The selected event is null')
      }
      const foundTask = getTaskById(String(selectedEvent.event_id))
      if (eventScope === EventScopes.CURRENT && hasRecurringWeekdays(foundTask?.scheduler_settings?.weekdays)) {
        if (foundTask && foundTask.scheduler_settings) {
          const dateStr = toISOStringYYYYMMDD(selectedEvent.start)
          const newSchedulerSettings = {
            ...foundTask.scheduler_settings,
            except_days: [...foundTask.scheduler_settings.except_days],
            except_days_descriptions: { ...foundTask.scheduler_settings.except_days_descriptions },
            except_days_users: { ...(foundTask.scheduler_settings.except_days_users || {}) },
          }
          newSchedulerSettings.except_days.push(dateStr)
          newSchedulerSettings.except_days.sort()
          const finalDescription =
            deleteReason === TASK_CANCEL_REASON_OTHER ? deleteDescription : deleteReason
          newSchedulerSettings.except_days_descriptions[dateStr] = finalDescription
          const userName = user?.state.firstName
            ? `${user.state.firstName} ${user.state.lastName || ''}`.trim()
            : user?.state.email || 'Unknown User'
          newSchedulerSettings.except_days_users[dateStr] = userName

          await RobotApi.updateTheEntireOdyssey(
            getTaskId(String(selectedEvent.event_id)),
            foundTask.missions,
            foundTask.robot_id,
            newSchedulerSettings,
            foundTask.name ? foundTask.name : 'Cleaning Task',
            foundTask.scenario,
            TASK_STATUSES.NEW
          )
        }
      } else {
        if (foundTask) {
          await RobotApi.updateOdysseyType(foundTask.id, TaskTypeEnum.DELETE_SCENARIO)
        } else {
          console.error(`There is not task with ID ${selectedEvent.event_id} to delete!`)
        }
      }
      setOpenDeleteScheduleDialog(false)
      setDeleteDescription('')
      setDeleteReason('')
      setSelectedEvent(null)
    } catch (e) {
      console.error(`Failed to delete scheduled task: ${e}`)
    }
    setRefreshKey(v => v + 1)
    setEventScope(EventScopes.CURRENT)
  }

  const prevRefreshCount = useRef(refreshKey)
  const getRemoteEvents = useCallback(
    async (start: Date, end: Date) => {
      const isManualRefresh = prevRefreshCount.current !== refreshKey
      prevRefreshCount.current = refreshKey

      if (isLoadingEvents.current) {
        return
      }
      isLoadingEvents.current = true
      const gen = ++normalizedFetchGenRef.current

      try {
        setIsGausiumSyncing(false)
        // Dictionaries load once on mount. Refetch them only after an explicit save/delete.
        if (isManualRefresh || !dataCache.current.fetched.robots) {
          await fetchInitialData(isManualRefresh)
        }

        const fetchStart = addDays(start, -2)
        const fetchEnd = addDays(end, 2)
        const robotIdsForServer = scopedRobotIds(filterState)
        const baseParams: {
          start_date: string
          end_date: string
          robot_ids?: number[]
        } = {
          start_date: fetchStart.toISOString(),
          end_date: fetchEnd.toISOString(),
          ...(robotIdsForServer !== undefined ? { robot_ids: robotIdsForServer } : {}),
        }

        // Fast path (like dashboard): Autonomics from DB + GS from DB/cache only — no external GS API.
        const [[autoRes, gsCacheRes], odysseyRows] = await Promise.all([
          Promise.all([
            MainApi.fetchUnifiedEvents({ ...baseParams, source: 'autonomics' }),
            GAUSIUM_ENABLED
              ? MainApi.fetchUnifiedEvents({
                  ...baseParams,
                  source: 'gausium_task_reports',
                  cache_only: true,
                }).catch((err: unknown) => {
                  console.warn('[TaskSchedulerNormalized] GS cache_only failed; Autonomics only', err)
                  return { results: [] as NormalizedStatisticsApiRow[], timings_ms: {} }
                })
              : Promise.resolve({ results: [] as NormalizedStatisticsApiRow[], timings_ms: {} }),
          ]),
          MainApi.fetchOdysseys(robotIdsForServer),
        ])
        dataCache.current.odysseys = odysseyRows
        odysseyPlanSigRef.current = odysseyPlanSignature(odysseyRows)

        if (gen !== normalizedFetchGenRef.current) {
          return
        }

        const autoRows = (autoRes.results ?? []) as NormalizedStatisticsApiRow[]

        const mergedRows = dedupeNormalizedApiRows([
          ...autoRows,
          ...((gsCacheRes.results ?? []) as NormalizedStatisticsApiRow[]),
        ])

        const ridMap = dataCache.current.robotIdToFacilityIds
        factRowsRef.current = mergedRows
        factSigRef.current = factsSignature(mergedRows)
        setNormalizedRecords(rowsToFilterableRecords(mergedRows, ridMap))
        const nextPlanned = generatePlannedEventsFromRobots(fetchStart, fetchEnd)
        plannedEventSigRef.current = plannedEventsSignature(nextPlanned)
        setPlannedEvents(nextPlanned)
        plansLoadedRef.current = true
        // Edit/create leaves status at `new` briefly; do not wait a full poll interval for Loading / Loaded.
        if (odysseyNeedsLiveStatusPoll(odysseyRows)) {
          window.setTimeout(() => {
            void pollOdysseyPlansRef.current()
          }, 1000)
        }

        if (GAUSIUM_ENABLED) {
          setIsGausiumSyncing(true)
          void runGausiumGapRefreshFollowup({
            debounceMs: GAUSIUM_GAP_DEBOUNCE_MS,
            firstPollDelayMs: GAUSIUM_GAP_FIRST_POLL_MS,
            pollIntervalMs: GAUSIUM_GAP_POLL_MS,
            maxTicks: GAUSIUM_GAP_MAX_TICKS,
            minTicksBeforeStable: 0,
            stableRounds: 0,
            queueRefresh: () => MainApi.queueGausiumNormalizedGapRefresh(baseParams),
            fetchSyncStatus: () => MainApi.fetchGausiumNormalizedSyncStatus(baseParams),
            initialCursor:
              (gsCacheRes as { snapshot_cursor?: string }).snapshot_cursor ??
              buildDeltaCursorFromRows((gsCacheRes.results ?? []) as NormalizedStatisticsApiRow[]),
            fetchDelta: cursor =>
              MainApi.fetchGausiumNormalizedDelta({
                ...baseParams,
                cursor,
                limit: 1000,
              }),
            isStale: () => gen !== normalizedFetchGenRef.current,
            onDelta: rows => {
              if (gen !== normalizedFetchGenRef.current) return
              const merged = mergeFactsByEventId(factRowsRef.current, rows as NormalizedStatisticsApiRow[])
              const sig = factsSignature(merged)
              factRowsRef.current = merged
              if (sig === factSigRef.current) return
              factSigRef.current = sig
              setNormalizedRecords(rowsToFilterableRecords(merged, dataCache.current.robotIdToFacilityIds))
            },
          }).finally(() => {
            if (gen !== normalizedFetchGenRef.current) return
            setIsGausiumSyncing(false)
          })
        }
      } catch (e) {
        console.error('[TaskSchedulerNormalized] load failed', e)
        setIsGausiumSyncing(false)
      } finally {
        isLoadingEvents.current = false
      }
    },
    [refreshKey, generatePlannedEventsFromRobots, filterState, scopedRobotIds]
  )

  const lastFetchedRange = useRef<{
    start: number
    end: number
    refreshCount: number
    robotScopeKey: string
  }>({
    start: 0,
    end: 0,
    refreshCount: -1,
    robotScopeKey: '',
  })

  React.useEffect(() => {
    if (hasAppliedInitialVendorDefaultRef.current || hasAutonomicsRobotsAvailable == null) return
    hasAppliedInitialVendorDefaultRef.current = true
    if (!hasAutonomicsRobotsAvailable) return
    if (filterState.vendor) return
    setFilterState({ ...filterState, vendor: VENDOR.AUTONOMICS })
  }, [hasAutonomicsRobotsAvailable, filterState, setFilterState])

  React.useEffect(() => {
    if (!catalogsReady) return
    const s = currentRange.start.getTime()
    const e = currentRange.end.getTime()
    const ids = scopedRobotIds(filterState)
    const robotScopeKey =
      ids === undefined
        ? ''
        : ids.length
        ? ids
            .slice()
            .sort((a, b) => a - b)
            .join(',')
        : '__empty__'
    if (
      s !== e &&
      (s !== lastFetchedRange.current.start ||
        e !== lastFetchedRange.current.end ||
        refreshKey !== lastFetchedRange.current.refreshCount ||
        robotScopeKey !== lastFetchedRange.current.robotScopeKey)
    ) {
      lastFetchedRange.current = { start: s, end: e, refreshCount: refreshKey, robotScopeKey }
      getRemoteEvents(currentRange.start, currentRange.end)
    }
  }, [catalogsReady, currentRange, getRemoteEvents, refreshKey, filterState, scopedRobotIds])

  const taskDialogOpen = isSettingsOpen || isEditConfirmationOpen || openDeleteScheduleDialog
  const taskDialogOpenRef = useRef(taskDialogOpen)
  taskDialogOpenRef.current = taskDialogOpen

  const pollOdysseyPlans = useCallback(async () => {
    if (
      !plansLoadedRef.current ||
      taskDialogOpenRef.current ||
      isLoadingEvents.current ||
      odysseyPollInFlightRef.current
    ) {
      return
    }
    const gen = normalizedFetchGenRef.current
    const fetchStart = addDays(currentRange.start, -2)
    const fetchEnd = addDays(currentRange.end, 2)
    const robotIdsForServer = scopedRobotIds(filterState)
    odysseyPollInFlightRef.current = true
    try {
      const rows = await MainApi.fetchOdysseys(robotIdsForServer)
      if (gen !== normalizedFetchGenRef.current || taskDialogOpenRef.current || isLoadingEvents.current) return
      const sourceSig = odysseyPlanSignature(rows)
      // Always re-clamp against `now` so a slot whose start just passed drops even when
      // the odyssey template signature is unchanged. Skip setState only if cards match.
      if (sourceSig !== odysseyPlanSigRef.current) {
        dataCache.current.odysseys = rows
        odysseyPlanSigRef.current = sourceSig
      }
      const nextPlanned = generatePlannedEventsFromRobots(fetchStart, fetchEnd)
      const eventSig = plannedEventsSignature(nextPlanned)
      if (eventSig === plannedEventSigRef.current) return
      plannedEventSigRef.current = eventSig
      setPlannedEvents(nextPlanned)
    } catch (e) {
      console.warn('[TaskSchedulerNormalized] odyssey plan poll failed', e)
    } finally {
      odysseyPollInFlightRef.current = false
    }
  }, [currentRange, filterState, generatePlannedEventsFromRobots, scopedRobotIds])

  pollOdysseyPlansRef.current = pollOdysseyPlans

  React.useEffect(() => {
    if (!catalogsReady) return
    let cancelled = false
    let timerId = 0
    const scheduleNext = () => {
      const delay = odysseyNeedsLiveStatusPoll(dataCache.current.odysseys)
        ? ODYSSEY_PLAN_POLL_FAST_MS
        : ODYSSEY_PLAN_POLL_MS
      timerId = window.setTimeout(() => {
        void (async () => {
          await pollOdysseyPlans()
          if (!cancelled) scheduleNext()
        })()
      }, delay)
    }
    scheduleNext()
    return () => {
      cancelled = true
      window.clearTimeout(timerId)
    }
  }, [catalogsReady, pollOdysseyPlans])

  const wasTaskDialogOpenRef = useRef(false)
  React.useEffect(() => {
    const wasOpen = wasTaskDialogOpenRef.current
    wasTaskDialogOpenRef.current = taskDialogOpen
    if (wasOpen && !taskDialogOpen) {
      void pollOdysseyPlans()
      void pollFactsRef.current()
    }
  }, [taskDialogOpen, pollOdysseyPlans])

  const pollFacts = useCallback(async () => {
    if (
      !plansLoadedRef.current ||
      taskDialogOpenRef.current ||
      isLoadingEvents.current ||
      factsPollInFlightRef.current
    ) {
      return
    }
    const hot = hotFactsWindow()
    if (!rangesOverlap(currentRange.start, currentRange.end, hot.start, hot.end)) return

    const gen = normalizedFetchGenRef.current
    const robotIdsForServer = scopedRobotIds(filterState)
    const baseParams: {
      start_date: string
      end_date: string
      robot_ids?: number[]
    } = {
      start_date: hot.start.toISOString(),
      end_date: hot.end.toISOString(),
      ...(robotIdsForServer !== undefined ? { robot_ids: robotIdsForServer } : {}),
    }
    factsPollInFlightRef.current = true
    try {
      const [autoRes, gsRes] = await Promise.all([
        MainApi.fetchUnifiedEvents({ ...baseParams, source: 'autonomics' }),
        GAUSIUM_ENABLED
          ? MainApi.fetchUnifiedEvents({
              ...baseParams,
              source: 'gausium_task_reports',
              cache_only: true,
            }).catch((err: unknown) => {
              console.warn('[TaskSchedulerNormalized] hot GS cache_only failed', err)
              return { results: [] as NormalizedStatisticsApiRow[] }
            })
          : Promise.resolve({ results: [] as NormalizedStatisticsApiRow[] }),
      ])
      if (gen !== normalizedFetchGenRef.current || taskDialogOpenRef.current || isLoadingEvents.current) return
      const incoming = dedupeNormalizedApiRows([
        ...((autoRes.results ?? []) as NormalizedStatisticsApiRow[]),
        ...((gsRes.results ?? []) as NormalizedStatisticsApiRow[]),
      ])
      const merged = mergeFactsByEventId(factRowsRef.current, incoming)
      const sig = factsSignature(merged)
      factRowsRef.current = merged
      if (sig === factSigRef.current) return
      factSigRef.current = sig
      setNormalizedRecords(rowsToFilterableRecords(merged, dataCache.current.robotIdToFacilityIds))
    } catch (e) {
      console.warn('[TaskSchedulerNormalized] hot facts poll failed', e)
    } finally {
      factsPollInFlightRef.current = false
    }
  }, [currentRange, filterState, scopedRobotIds])

  pollFactsRef.current = pollFacts

  React.useEffect(() => {
    if (!catalogsReady) return
    let cancelled = false
    let timerId = 0
    const scheduleNext = () => {
      timerId = window.setTimeout(() => {
        void (async () => {
          await pollFacts()
          if (!cancelled) scheduleNext()
        })()
      }, FACTS_POLL_MS)
    }
    scheduleNext()
    return () => {
      cancelled = true
      window.clearTimeout(timerId)
    }
  }, [catalogsReady, pollFacts])

  const handleClearFilters = () => {
    setUrl({
      vendor: '',
      facility: '',
      robot: '',
      rfav: false,
      mission: '',
      startAfter: -1,
    })
    setDisplayOffsetMinutes(-new Date().getTimezoneOffset())
  }

  const hasActiveFilters = useMemo(() => {
    const browserOffsetMinutes = -new Date().getTimezoneOffset()
    return !!(
      filterState.facilityId ||
      filterState.vendor ||
      filterState.robotSn ||
      favoritesOnly ||
      filterState.missionKey ||
      filterState.startAfterHour >= 0 ||
      displayOffsetMinutes !== browserOffsetMinutes
    )
  }, [filterState, displayOffsetMinutes, favoritesOnly])

  const handleExportSchedule = () => {
    let rangeStart = startOfDay(selectedDate)
    let rangeEnd = endOfDay(selectedDate)

    if (view === Views.WEEK) {
      rangeStart = startOfWeek(selectedDate, { weekStartsOn: 1 })
      rangeEnd = endOfWeek(selectedDate, { weekStartsOn: 1 })
    } else if (view === Views.MONTH) {
      rangeStart = startOfMonth(selectedDate)
      rangeEnd = endOfMonth(selectedDate)
    }

    const start = rangeStart.getTime()
    const end = rangeEnd.getTime()

    const exportEvents = filteredCalendarEvents.filter(event => {
      const s = event.start.getTime()
      const e = event.end.getTime()
      return s <= end && e >= start
    })

    exportScheduleToCSV(exportEvents)
  }

  return (
    <>
      <div className={schedulerStyle.mainView}>
        {isGausiumSyncing && (
          <div className={nonBlockingSyncLoaderStyle.nonBlockingSyncLoader} title="Gausium background sync in progress">
            <Loader />
          </div>
        )}
        <div className={schedulerStyle.titleBlock}>
          <Title text={t('header.calendar')} />
          <div className={schedulerStyle.headerButtons}>
            <CustomButton.Outlined
              className={schedulerStyle.btn}
              style={{ width: isMobile ? 'auto' : '150px' }}
              onClick={handleExportSchedule}
            >
              Export
            </CustomButton.Outlined>
            <CustomButton.Standart
              className={schedulerStyle.btn}
              style={{ width: isMobile ? 'auto' : '250px' }}
              onClick={() => {
                setSelectedEvent(null)
                setIsSettingsOpen(true)
              }}
            >
              New Task
            </CustomButton.Standart>
            {/* intentionally no extra hints here */}
          </div>
        </div>

        <div className={schedulerStyle.filtersBlock}>
          <div className={schedulerStyle.timezoneRow}>
            {/* <Dropdown
              className={schedulerStyle.filterTimezone}
              minWidth={isMobile ? 'auto' : 160}
              useSmallDropdownStyle={isMobile}
              currentOption={{
                value: displayOffsetMinutes,
                text: formatUtcOffset(displayOffsetMinutes),
              }}
              placeholder={formatUtcOffset(displayOffsetMinutes)}
              options={UTC_OFFSET_OPTIONS}
              onClickOption={(opt: IOption) => setDisplayOffsetMinutes(Number(opt.value))}
            /> */}
            {isMobile && (
              <CustomButton.Outlined
                className={clsx(schedulerStyle.filterToggle, {
                  [schedulerStyle.filterToggleActive]: hasActiveFilters,
                })}
                onClick={() => setShowFilters(!showFilters)}
              >
                Filters {showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </CustomButton.Outlined>
            )}
          </div>

          {(showFilters || !isMobile) && (
            <div className={schedulerStyle.filters}>
              <Dropdown
                className={mapPageFilterDropdown.filterDropdown}
                minWidth={isMobile ? 'auto' : 300}
                useSmallDropdownStyle={isMobile}
                canCopy={true}
                currentOption={
                  filterState.facilityId
                    ? {
                        value: filterState.facilityId,
                        text:
                          filterOptions.facilityOptions.find(f => String(f.id) === filterState.facilityId)?.name ||
                          `Facility ${filterState.facilityId}`,
                      }
                    : undefined
                }
                placeholder="All Facilities"
                options={facilityDropdownOptions}
                onClickOption={(opt: IOption) => setFilterState(s => ({ ...s, facilityId: String(opt.value) }))}
              />

              <Dropdown
                className={mapPageFilterDropdown.filterDropdownNarrow}
                minWidth={isMobile ? 'auto' : 300}
                useSmallDropdownStyle={isMobile}
                canCopy={true}
                currentOption={
                  filterState.vendor
                    ? {
                        value: filterState.vendor,
                        text: getVendorDisplayName(filterState.vendor as VendorCode) || filterState.vendor,
                      }
                    : undefined
                }
                placeholder="All Vendors"
                options={vendorDropdownOptions}
                onClickOption={(opt: IOption) => setFilterState(s => ({ ...s, vendor: opt.value as VendorCode | '' }))}
              />

              <Dropdown
                className={mapPageFilterDropdown.filterDropdownMedium}
                minWidth={isMobile ? 'auto' : 400}
                useSmallDropdownStyle={isMobile}
                canCopy={true}
                currentOption={
                  filterState.robotSn
                    ? {
                        value: filterState.robotSn,
                        text: (() => {
                          const currentRobot = filterOptions.robots.find(r => r.sn === filterState.robotSn)
                          if (!currentRobot) return filterState.robotSn
                          return currentRobot.name && currentRobot.name !== currentRobot.sn
                            ? `${currentRobot.sn} - ${currentRobot.name}`
                            : currentRobot.sn
                        })(),
                      }
                    : favoritesOnly
                      ? {
                          value: FAVORITES_DROPDOWN_VALUE,
                          text: t('favorites.only'),
                          leadingIcon: 'star',
                        }
                      : undefined
                }
                placeholder="All Robots"
                options={robotDropdownOptions}
                onClickOption={(opt: IOption) => {
                  const value = String(opt.value ?? '')
                  if (value === FAVORITES_DROPDOWN_VALUE) {
                    setUrl({ robot: '', rfav: true })
                    return
                  }
                  setUrl({ robot: value, rfav: false })
                }}
              />

              <Dropdown
                className={mapPageFilterDropdown.filterDropdown}
                minWidth={isMobile ? 'auto' : 400}
                useSmallDropdownStyle={isMobile}
                canCopy={true}
                currentOption={missionFilterCurrentOption}
                placeholder="All Missions"
                options={missionSelectOptions}
                onClickOption={(opt: IOption) => setFilterState(s => ({ ...s, missionKey: String(opt.value) }))}
              />

              <Dropdown
                className={mapPageFilterDropdown.filterDropdownMedium}
                minWidth={isMobile ? 'auto' : 180}
                useSmallDropdownStyle={isMobile}
                currentOption={{
                  value: filterState.startAfterHour,
                  text:
                    filterState.startAfterHour < 0
                      ? 'Start After: 00:00'
                      : `Start After: ${
                          filterState.startAfterHour < 10
                            ? `0${filterState.startAfterHour}`
                            : filterState.startAfterHour
                        }:00`,
                }}
                placeholder="Start After: 00:00"
                options={[
                  ...Array.from({ length: 24 }, (_, i) => ({
                    value: i,
                    text: `Start After: ${i < 10 ? `0${i}` : i}:00`,
                  })),
                ]}
                onClickOption={(opt: IOption) => setFilterState(s => ({ ...s, startAfterHour: Number(opt.value) }))}
              />

              <CustomButton.Outlined className={schedulerStyle.clearButton} onClick={handleClearFilters}>
                Clear filters
              </CustomButton.Outlined>
            </div>
          )}
        </div>

        {favoritesOnly && catalogsReady && favoriteRobotIds.length === 0 ? (
          <div className={schedulerStyle.favoritesEmpty}>
            <div className={schedulerStyle.favoritesEmptyTitle}>{t('favorites.empty_robots')}</div>
            <div className={schedulerStyle.favoritesEmptyDescription}>{t('favorites.empty_analytics_description')}</div>
          </div>
        ) : (
        <div className={schedulerStyle.calendarWrapper}>
          <Calendar
            localizer={localizer}
            events={agendaEvents}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            view={view}
            onView={handleViewChange}
            date={selectedDate}
            onNavigate={handleNavigate}
            length={view === Views.AGENDA ? 1 : undefined}
            min={calendarTimeBounds.min}
            max={calendarTimeBounds.max}
            scrollToTime={calendarTimeBounds.scrollToTime}
            enableAutoScroll={false}
            formats={calendarFormats}
            eventPropGetter={eventPropGetter}
            onSelectEvent={handleEventClick}
            tooltipAccessor={(e: any) => e.title}
            components={{
              event: ({ event }: { event: ProcessedEvent }) => {
                const liveStatus = event.extendedProps?.liveStatus
                const statusMsg = getLoadingStatus(liveStatus)
                const isFuture = event.start.getTime() >= Date.now()
                const isNotLoaded = isFuture && statusMsg === 'Not Loaded'
                const isLoading = isFuture && statusMsg === 'Loading'

                return (
                  <div
                    title={typeof event.title === 'string' ? event.title : undefined}
                    style={{
                      fontSize: '11px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      pointerEvents: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      lineHeight: '1.2',
                    }}
                  >
                    {isLoading && (
                      <SyncIcon
                        sx={{
                          fontSize: '12px',
                          color: '#fff',
                          animation: 'spin 2s linear infinite',
                          '@keyframes spin': { '100%': { transform: 'rotate(-360deg)' } },
                        }}
                      />
                    )}
                    {isNotLoaded && <ErrorRoundedIcon sx={{ fontSize: '13px', color: '#fff' }} />}
                    {event.extendedProps?.statusPrefix}
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{event.title}</span>
                  </div>
                )
              },
            }}
          />
        </div>
        )}

        <Popover
          open={Boolean(anchorEl) && Boolean(livePopoverEvent)}
          anchorEl={anchorEl}
          onClose={handleClosePopover}
          disableRestoreFocus
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{
            sx: { p: 0, borderRadius: 2, minWidth: 280, maxWidth: 400 },
          }}
        >
          {livePopoverEvent && (
            <Box>
              <Box sx={{ height: '6px', bgcolor: livePopoverEvent.color || '#3174ad' }} />
              <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    {livePopoverEvent.extendedProps?.statusPrefix}
                    {livePopoverEvent.title}
                  </Typography>
                  <IconButton size="small" onClick={handleClosePopover}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>

                {(() => {
                  const task = selectedEvent ? getTaskById(String(selectedEvent.event_id)) : null
                  const loadingMsg = task ? getLoadingStatus(task.status) : ''
                  if (!loadingMsg || loadingMsg === 'Loaded' || livePopoverEvent.start.getTime() < Date.now()) return null
                  const isLoading = loadingMsg === 'Loading'
                  return (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1,
                        p: '4px 10px',
                        borderRadius: '16px',
                        width: 'fit-content',
                        bgcolor: isLoading ? 'rgba(245,158,11,0.12)' : 'rgba(239,68,68,0.12)',
                        border: isLoading ? '1px solid rgba(245,158,11,0.4)' : '1px solid rgba(239,68,68,0.4)',
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          flexShrink: 0,
                          bgcolor: isLoading ? '#f59e0b' : '#ef4444',
                          animation: isLoading ? 'pulse 1.4s ease-in-out infinite' : 'none',
                          '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.4 } },
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: isLoading ? '#f59e0b' : '#ef4444',
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          fontSize: '10px',
                        }}
                      >
                        {loadingMsg}
                      </Typography>
                    </Box>
                  )
                })()}

                <TaskEventViewer event={livePopoverEvent} />
                <Divider sx={{ my: 2, borderColor: 'var(--form-border)' }} />
                {livePopoverEvent.extendedProps?.source_kind === 'planned' &&
                  livePopoverEvent.extendedProps?.status !== 'deleted' &&
                  livePopoverEvent.end.getTime() > Date.now() &&
                  (() => {
                    const task = selectedEvent ? getTaskById(String(selectedEvent.event_id)) : null
                    const robot = task ? dataCache.current.robots.find(r => r.id === task.robot_id) : null
                    const isBlocked = task
                      ? getLoadingStatus(task.status) !== 'Loaded' && robot?.network_status === 'online'
                      : false
                    return (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                        <CustomButton.Outlined
                          className={schedulerStyle.btn}
                          style={{
                            width: '100px',
                            opacity: isBlocked ? 0.3 : 1,
                            pointerEvents: isBlocked ? 'none' : 'auto',
                          }}
                          onClick={() => {
                            handleClosePopover()
                            const isSingle = isAllValueDisabled(selectedEvent)
                            if (isSingle) {
                              setSingleTaskEditing(true)
                              setIsSettingsOpen(true)
                              setEventScope(EventScopes.CURRENT)
                            } else {
                              setIsEditConfirmationOpen(true)
                            }
                          }}
                          disabled={isBlocked}
                        >
                          {t('common.edit')}
                        </CustomButton.Outlined>
                        <CustomButton.Outlined
                          className={schedulerStyle.btn}
                          style={{
                            width: '100px',
                            color: '#ff4d4f',
                            borderColor: '#ff4d4f',
                            opacity: isBlocked ? 0.3 : 1,
                            pointerEvents: isBlocked ? 'none' : 'auto',
                          }}
                          onClick={() => {
                            handleClosePopover()
                            setDeleteDescription('')
                            setDeleteReason('')
                            setOpenDeleteScheduleDialog(true)
                          }}
                          disabled={isBlocked}
                        >
                          {t('common.delete')}
                        </CustomButton.Outlined>
                      </Box>
                    )
                  })()}
              </Box>
            </Box>
          )}
        </Popover>
        {isEditConfirmationOpen && (
          <ConfirmationDialogAllSingle
            confirmText={'Ok'}
            cancelText="Cancel"
            open={true}
            title={'Edit the scheduled task?'}
            submitting={undefined}
            onClose={() => {
              setIsEditConfirmationOpen(false)
              setEventScope(EventScopes.CURRENT)
              setSelectedEvent(null)
            }}
            onSubmit={() => {
              if (eventScope === EventScopes.ALL) setSingleTaskEditing(false)
              else if (eventScope === EventScopes.CURRENT) setSingleTaskEditing(true)
              else throw new Error(`Unknown event scope: ${eventScope}`)
              setIsSettingsOpen(true)
              setIsEditConfirmationOpen(false)
              setEventScope(EventScopes.CURRENT)
            }}
          >
            <EventEditDeletePopup
              currentValue={EventScopes.CURRENT}
              allValue={EventScopes.ALL}
              value={eventScope}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setEventScope(event.target.value)}
            />
          </ConfirmationDialogAllSingle>
        )}
        {(() => {
          const taskForEdit = selectedEvent ? getTaskById(String(selectedEvent.event_id)) : undefined
          const target: DialogTarget = taskForEdit
            ? isSingleTaskEditing
              ? { kind: 'task_occurrence', taskId: taskForEdit.id }
              : { kind: 'task_all', taskId: taskForEdit.id }
            : { kind: 'new' }
          return (
            <TaskSettingsModal
              isOpen={isSettingsOpen}
              onClose={onSettingsCloseClicked}
              taskName={'Cleaning Task'}
              target={target}
              preselectedData={taskForEdit ? { task: taskForEdit } : undefined}
              dateToEdit={isSingleTaskEditing && selectedEvent ? selectedEvent.start : null}
              onSubmit={onSettingsSubmitClicked}
              displayOffsetMinutes={displayOffsetMinutes}
            />
          )
        })()}
        {openDeleteScheduleDialog && (
          <ConfirmationDialogAllSingle
            confirmText={isAllValueDisabled(selectedEvent) ? t('common.delete') : 'Ok'}
            cancelText="Cancel"
            open={openDeleteScheduleDialog}
            isSmall={isAllValueDisabled(selectedEvent)}
            danger={true}
            title={isAllValueDisabled(selectedEvent) ? 'Delete the task?' : 'Delete the scheduled task?'}
            disableConfirm={
              !isAllValueDisabled(selectedEvent) &&
              eventScope === EventScopes.CURRENT &&
              (!deleteReason || (deleteReason === TASK_CANCEL_REASON_OTHER && !deleteDescription))
            }
            submitting={undefined}
            onClose={() => {
              setOpenDeleteScheduleDialog(false)
              setEventScope(EventScopes.CURRENT)
              setDeleteDescription('')
              setDeleteReason('')
              setSelectedEvent(null)
            }}
            onSubmit={handleSubmitDeleteSchedule}
          >
            {!isAllValueDisabled(selectedEvent) && (
              <>
                <EventEditDeletePopup
                  currentValue={EventScopes.CURRENT}
                  allValue={EventScopes.ALL}
                  value={eventScope}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => setEventScope(event.target.value)}
                />
                {eventScope === EventScopes.CURRENT && (
                  <Box sx={{ marginBottom: '30px' }}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'var(--text-secondary)' }}>
                        Reason
                      </Typography>
                      <Dropdown
                        placeholder="Select Reason"
                        currentOption={deleteReason ? { value: deleteReason, text: deleteReason } : undefined}
                        options={[...TASK_CANCEL_REASON_OPTIONS]}
                        onClickOption={(opt: IOption) => setDeleteReason(String(opt.value))}
                      />
                    </Box>
                    {deleteReason === TASK_CANCEL_REASON_OTHER && (
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="Description"
                        value={deleteDescription}
                        onChange={e => setDeleteDescription(e.target.value)}
                        placeholder="Please provide a reason..."
                        required
                      />
                    )}
                  </Box>
                )}
              </>
            )}
          </ConfirmationDialogAllSingle>
        )}
      </div>
    </>
  )
}

export default TaskSchedulerNormalized
